import type { InspectionBook } from "./inspection-book.js";
import type { ValidationIssue } from "./validation-issue.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";

export type ReleaseGateResult =
  | 'RELEASED'
  | 'BLOCKED'
  | 'RELEASED_WITH_ENGINEER_OVERRIDE';

export interface ReleaseGateEvaluation {
  readonly result: ReleaseGateResult;
  readonly canIssue: boolean;
  readonly blockerIssues: readonly ValidationIssue[];
  readonly openIssuesCount: number;
  readonly overrideIssuesCount: number;
  readonly blockingReasons: readonly string[];
}

export class InspectionBookReleaseGate {
  public evaluate(
    book: InspectionBook,
    issues: readonly ValidationIssue[]
  ): ReleaseGateEvaluation {
    const openBlockers = issues.filter(i => i.severity === 'BLOCKER' && i.status === 'OPEN');
    const criticalOpen = issues.filter(i => i.severity === 'CRITICAL' && i.status === 'OPEN');
    const overridden = issues.filter(i => i.status === 'ENGINEER_OVERRIDE');
    const openIssues = issues.filter(i => i.status === 'OPEN' || i.status === 'REOPENED');

    const blockingReasons: string[] = [];

    for (const b of openBlockers) {
      blockingReasons.push(`[BLOCKER] ${b.title}: ${b.description}`);
    }

    // Check if book has items marked NOT_APPLICABLE without justification
    const unreasonedNA = book.items.filter(
      item => item.applicability === 'NOT_APPLICABLE' && (!item.notApplicableReason || item.notApplicableReason.trim().length === 0)
    );
    if (unreasonedNA.length > 0) {
      blockingReasons.push(`[DOCUMENTAL] ${unreasonedNA.length} item(s) marcado(s) como N/A sem justificativa técnica obrigatória.`);
    }

    if (openBlockers.length > 0 || unreasonedNA.length > 0) {
      return {
        result: 'BLOCKED',
        canIssue: false,
        blockerIssues: openBlockers,
        openIssuesCount: openIssues.length,
        overrideIssuesCount: overridden.length,
        blockingReasons,
      };
    }

    if (overridden.length > 0) {
      return {
        result: 'RELEASED_WITH_ENGINEER_OVERRIDE',
        canIssue: true,
        blockerIssues: [],
        openIssuesCount: openIssues.length,
        overrideIssuesCount: overridden.length,
        blockingReasons: [],
      };
    }

    return {
      result: 'RELEASED',
      canIssue: true,
      blockerIssues: [],
      openIssuesCount: openIssues.length,
      overrideIssuesCount: 0,
      blockingReasons: [],
    };
  }

  public assertCanIssue(
    book: InspectionBook,
    issues: readonly ValidationIssue[],
    isEngineer: boolean,
    hasOverridePermission: boolean
  ): ReleaseGateEvaluation {
    const evalResult = this.evaluate(book, issues);

    if (!evalResult.canIssue) {
      throw new AppError(
        ErrorCode.Validation,
        `Emissão do Book de Inspeção Bloqueada:\n${evalResult.blockingReasons.join('\n')}`
      );
    }

    if (evalResult.result === 'RELEASED_WITH_ENGINEER_OVERRIDE') {
      if (!isEngineer && !hasOverridePermission) {
        throw new AppError(
          ErrorCode.Authorization,
          'Apenas Engenheiro / PLH autorizado pode emitir Book de Inspeção com pendências liberadas sob override.'
        );
      }
    }

    return evalResult;
  }
}

export const inspectionBookReleaseGate = new InspectionBookReleaseGate();
